import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCheck, Check, Clock, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/** Formats an ISO timestamp as "Feb 09, 2026 · 22:31" in IST. */
function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
}

/**
 * WhatsApp-style Message Info modal — shows a per-recipient breakdown
 * of read / delivered / pending states for a single message.
 */
export function MessageInfoModal({ messageId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data: fresh } = await api.get(`/messages/${messageId}/info`);
        if (!cancelled) setData(fresh);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [messageId]);

  const body = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4"
      onMouseDown={onClose}
      data-testid="message-info-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="message-info-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#DCDCD3] bg-[#F5F5F0]">
          <div>
            <div className="text-sm font-semibold text-[#1C1C19]">Message Info</div>
            {data?.sent_at && (
              <div className="text-[11px] text-[#6B6B61] font-mono mt-0.5">
                Sent · {fmtWhen(data.sent_at)}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="message-info-close"
            className="p-1.5 rounded-full text-[#6B6B61] hover:bg-white transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {data?.text_preview && (
          <div className="px-5 py-2 border-b border-[#F5F5F0] bg-white">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B61]">
              Preview
            </div>
            <div className="text-xs text-[#1C1C19] mt-1 line-clamp-2">
              {data.text_preview}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-10 text-[#6B6B61]">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}
          {error && (
            <div className="px-5 py-4 text-xs text-[#C8553D]" data-testid="message-info-error">
              {error}
            </div>
          )}
          {!loading && !error && data && (
            <>
              <Section
                icon={<CheckCheck className="w-4 h-4 text-[#2563EB]" strokeWidth={2} />}
                title={`Read by · ${data.read.length}`}
                emptyText="No one has read this yet"
                items={data.read.map((r) => ({
                  name: r.display_name,
                  when: fmtWhen(r.read_at),
                }))}
                testid="message-info-read-section"
              />
              <Section
                icon={<CheckCheck className="w-4 h-4 text-[#6B6B61]" strokeWidth={2} />}
                title={`Delivered to · ${data.delivered.length}`}
                emptyText="No one else pending delivery"
                items={data.delivered.map((r) => ({
                  name: r.display_name,
                  when: fmtWhen(r.delivered_at),
                }))}
                testid="message-info-delivered-section"
              />
              <Section
                icon={<Clock className="w-4 h-4 text-[#8A6046]" strokeWidth={2} />}
                title={`Pending · ${data.pending.length}`}
                emptyText="Everyone has received the message"
                items={data.pending.map((r) => ({ name: r.display_name, when: "offline" }))}
                testid="message-info-pending-section"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}

function Section({ icon, title, items, emptyText, testid }) {
  return (
    <div className="px-5 py-3 border-b border-[#F5F5F0] last:border-b-0" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <div className="text-xs font-semibold text-[#1C1C19]">{title}</div>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-[#6B6B61] italic pl-6">{emptyText}</div>
      ) : (
        <div className="space-y-1 pl-6">
          {items.map((it, i) => (
            <div
              key={`${it.name}-${i}`}
              className="flex items-center justify-between text-xs"
              data-testid="message-info-recipient-row"
            >
              <span className="text-[#1C1C19] truncate">{it.name}</span>
              <span className="text-[10px] text-[#6B6B61] font-mono flex-shrink-0 ml-2">
                {it.when}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Small inline tick indicator for the sender's own bubbles.
 * - ✓ gray: sent, not delivered yet
 * - ✓✓ gray: delivered to at least one recipient
 * - ✓✓ blue: read by ALL recipients (excluding sender)
 */
export function BubbleTicks({ deliveredCount, readCount, recipientCount, mine, pending }) {
  if (!mine || pending) return null;
  // Legacy messages (no receipt hydration) — hide ticks rather than
  // showing a misleading "not delivered" state.
  if (recipientCount == null) return null;
  // No recipients (empty group / sender is the only one) — no ticks.
  if (recipientCount === 0) return null;
  const allRead = (readCount || 0) >= recipientCount;
  const anyDelivered = (deliveredCount || 0) > 0 || (readCount || 0) > 0;
  if (allRead) {
    return (
      <span
        title={`Read by all ${recipientCount} recipient(s)`}
        data-testid="bubble-tick-read"
        className="inline-flex items-center"
      >
        <CheckCheck className="w-3.5 h-3.5 text-[#5AB2FF]" strokeWidth={2.5} />
      </span>
    );
  }
  if (anyDelivered) {
    return (
      <span
        title={`Delivered to ${deliveredCount || readCount || 0}/${recipientCount}`}
        data-testid="bubble-tick-delivered"
        className="inline-flex items-center"
      >
        <CheckCheck className="w-3.5 h-3.5 text-white/70" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span
      title="Sent"
      data-testid="bubble-tick-sent"
      className="inline-flex items-center"
    >
      <Check className="w-3.5 h-3.5 text-white/70" strokeWidth={2.5} />
    </span>
  );
}
